/**
 * Back-in-stock notification form.
 *
 * Submits the buy-buttons "notify me when back in stock" form over fetch so the
 * shopper stays on the product page (no reload), keeping the selected sold-out
 * variant in context, and shows an inline success message when done.
 *
 * The submission is remembered per product + variant in localStorage, so the
 * success message keeps showing instead of the form after a page refresh or when
 * the shopper returns to the same sold-out variant.
 *
 * Falls back to a normal form POST when JavaScript is unavailable.
 */
if (!customElements.get('back-in-stock-form')) {
  customElements.define(
    'back-in-stock-form',
    class BackInStockForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form || this.form.dataset.bisBound) return;
        this.form.dataset.bisBound = 'true';
        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        // Already requested for this variant in a previous visit/refresh.
        if (this.alreadyRequested()) {
          this.showSuccess(false);
        }
      }

      get storageKey() {
        return `back-in-stock:${this.dataset.productId || ''}:${this.dataset.variantId || ''}`;
      }

      alreadyRequested() {
        try {
          return window.localStorage.getItem(this.storageKey) === '1';
        } catch (error) {
          return false;
        }
      }

      remember() {
        try {
          window.localStorage.setItem(this.storageKey, '1');
        } catch (error) {
          // Storage unavailable (e.g. private mode) — ignore.
        }
      }

      showSuccess(focus = true) {
        const body = this.querySelector('[data-bis-body]');
        const success = this.querySelector('[data-bis-success]');

        if (body) body.hidden = true;
        if (success) {
          success.hidden = false;
          if (focus) success.focus();
        }
      }

      async handleSubmit(event) {
        // Let the browser's native validation block invalid submissions
        // (required fields, email format, privacy consent checkbox).
        if (!this.form.checkValidity()) return;

        event.preventDefault();

        const button = this.form.querySelector('[type="submit"]');
        if (button) button.disabled = true;

        try {
          await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
          });
        } catch (error) {
          // Network error — let the shopper try again.
          if (button) button.disabled = false;
          return;
        }

        this.remember();
        this.showSuccess();
      }
    }
  );
}
